import { cn } from '../../lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Edit, Plus, Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';

export function ExceptionsTab({
  loading, exceptions, exceptionStatus, setExceptionStatus,
  selectedExceptionKeys, setSelectedExceptionKeys, bulkActionLoading,
  handleBulkExceptionAction, toggleAllExceptions, toggleExceptionSelected, exceptionKey,
  handleOpenCorrection, openConvertModal,
}) {
  return (
    <>
                {/* Status filter + bulk action bar */}
                <div className="glass-card p-4 flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <Label className="text-sm font-medium mr-1">View:</Label>
                    {[
                      { v: 'active', l: 'Active' },
                      { v: 'seen', l: 'Seen' },
                      { v: 'resolved', l: 'Resolved' },
                      { v: 'dismissed', l: 'Dismissed' },
                      { v: 'all', l: 'All' },
                    ].map(({ v, l }) => (
                      <Button
                        key={v}
                        variant={exceptionStatus === v ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => { setExceptionStatus(v); setSelectedExceptionKeys(new Set()); }}
                        data-testid={`exc-status-${v}`}
                      >
                        {l}
                      </Button>
                    ))}
                  </div>
                  {exceptionStatus === 'active' && exceptions.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground" data-testid="bulk-selected-count">
                        {selectedExceptionKeys.size} selected
                      </span>
                      <Button variant="outline" size="sm" onClick={() => handleBulkExceptionAction('seen')} disabled={bulkActionLoading || selectedExceptionKeys.size === 0} data-testid="bulk-seen-btn">
                        <Eye className="w-4 h-4 mr-1" /> Mark Seen
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleBulkExceptionAction('resolved')} disabled={bulkActionLoading || selectedExceptionKeys.size === 0} data-testid="bulk-resolved-btn">
                        <CheckCircle className="w-4 h-4 mr-1" /> Mark Resolved
                      </Button>
                      <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => handleBulkExceptionAction('dismissed')} disabled={bulkActionLoading || selectedExceptionKeys.size === 0} data-testid="bulk-dismiss-btn">
                        <EyeOff className="w-4 h-4 mr-1" /> Dismiss
                      </Button>
                    </div>
                  )}
                </div>

                <div className="glass-card table-scroll">
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : exceptions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <CheckCircle className="w-12 h-12 text-emerald-400 mb-4" />
                      <p className="text-lg font-medium">No {exceptionStatus === 'all' ? '' : exceptionStatus} exceptions</p>
                      <p className="text-muted-foreground">
                        {exceptionStatus === 'active' ? 'All caught up — well done!' : 'Try a different filter'}
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {exceptionStatus === 'active' && (
                            <TableHead className="w-10">
                              <input
                                type="checkbox"
                                checked={selectedExceptionKeys.size === exceptions.length && exceptions.length > 0}
                                onChange={toggleAllExceptions}
                                className="cursor-pointer"
                                data-testid="select-all-exceptions"
                              />
                            </TableHead>
                          )}
                          <TableHead>Employee</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Day</TableHead>
                          <TableHead>Clock In</TableHead>
                          <TableHead>Clock Out</TableHead>
                          <TableHead>Issues</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Audit</TableHead>
                          <TableHead className="w-32">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {exceptions.map((record, idx) => {
                          const k = exceptionKey(record);
                          const ex_status = record.exception_status || 'active';
                          return (
                            <TableRow key={`${record.employee_id}-${record.shift_date || record.date}-${idx}`}
                                      className={cn(
                                        ex_status === 'active' && 'bg-red-500/5',
                                        ex_status === 'seen' && 'bg-amber-500/5',
                                        ex_status === 'resolved' && 'bg-emerald-500/5',
                                        ex_status === 'dismissed' && 'bg-slate-500/5 opacity-70'
                                      )}>
                              {exceptionStatus === 'active' && (
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    checked={selectedExceptionKeys.has(k)}
                                    onChange={() => toggleExceptionSelected(record)}
                                    className="cursor-pointer"
                                    data-testid={`select-exc-${record.employee_id}-${record.shift_date}`}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <div>
                                  <p className="font-medium">{record.employee_name}</p>
                                  <p className="text-xs text-muted-foreground">{record.department}</p>
                                </div>
                              </TableCell>
                              <TableCell>{record.shift_date || record.date}</TableCell>
                              <TableCell>{record.day_of_week}</TableCell>
                              <TableCell className={cn(!record.first_clock_in && "text-red-400")}>
                                {record.first_clock_in?.slice(0, 5) || 'Missing'}
                              </TableCell>
                              <TableCell className={cn(!record.last_clock_out && "text-red-400")}>
                                {record.last_clock_out?.slice(0, 5) || 'Missing'}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {(record.exceptions || []).map((ex) => (
                                    <Badge key={ex} variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                                      {ex}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={cn(
                                  ex_status === 'active' && 'bg-red-500/20 text-red-400 border-red-500/30',
                                  ex_status === 'seen' && 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                                  ex_status === 'resolved' && 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                                  ex_status === 'dismissed' && 'bg-slate-500/20 text-slate-400 border-slate-500/30',
                                )} data-testid={`exc-row-status-${record.employee_id}-${record.shift_date}`}>
                                  {ex_status}
                                  {record.exception_resolution_action === 'leave_created' && ' (leave)'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {record.exception_seen_by && (
                                  <div>seen by {record.exception_seen_by}</div>
                                )}
                                {record.exception_resolved_by && (
                                  <div>resolved by {record.exception_resolved_by}</div>
                                )}
                                {record.exception_resolution_leave_type && (
                                  <div className="text-emerald-400">→ {record.exception_resolution_leave_type}</div>
                                )}
                                {record.exception_notes && (
                                  <div className="italic">"{record.exception_notes}"</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => handleOpenCorrection(record)} data-testid={`fix-${record.employee_id}-${record.shift_date || record.date}`}
                                          title="Correct clock-in/out">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {ex_status !== 'resolved' && ex_status !== 'dismissed' && (
                                    <Button variant="ghost" size="icon" className="text-emerald-400" onClick={() => openConvertModal(record)} data-testid={`convert-leave-${record.employee_id}-${record.shift_date}`}
                                            title="Convert to Leave">
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
    </>
  );
}
